import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["nav", "frame"];

  connect() {
    this.navTarget.addEventListener("click", this.onNavClick);
    this.updateCurrent();
    this.scrollToCurrent();
  }

  disconnect() {
    this.navTarget.removeEventListener("click", this.onNavClick);
  }

  frameTargetConnected(frame) {
    frame.addEventListener(
      "turbo:before-fetch-request",
      this.saveScrollPosition,
    );
    frame.addEventListener("turbo:frame-load", this.onFrameLoad);
  }

  frameTargetDisconnected(frame) {
    frame.removeEventListener(
      "turbo:before-fetch-request",
      this.saveScrollPosition,
    );
    frame.removeEventListener("turbo:frame-load", this.onFrameLoad);
  }

  toggleSidebar() {
    const layout = this.element.closest("docs-layout");
    if (!layout) return;
    layout.toggleAttribute("data-sidebar-open");
  }

  onNavClick = (event) => {
    const link = event.target.closest("a");
    if (!link) return;

    // On mobile, close the sidebar when a nav link is clicked
    if (window.innerWidth < 768) {
      const layout = this.element.closest("docs-layout");
      if (layout) layout.removeAttribute("data-sidebar-open");
    }

    // Auto-open the parent <details> when clicking a tag link
    const details = link.closest("details");
    if (details && !details.open) {
      details.open = true;
    }

    const url = new URL(link.href, window.location.origin);
    this._clickedHref = url.pathname;

    // Hash links don't trigger a frame load, so update highlights immediately
    if (url.hash) {
      this._clickedHash = url.hash;
      requestAnimationFrame(() => this.updateCurrent());
    }
  };

  saveScrollPosition = () => {
    const sidebar = this.element.querySelector("docs-sidebar");
    if (sidebar) {
      this._savedSidebarScroll = sidebar.scrollTop;
    }
  };

  onFrameLoad = () => {
    this.updateCurrent();
    if (this._savedSidebarScroll !== undefined) {
      this.restoreScrollPosition();
    } else {
      this.scrollToCurrent();
    }
    this._savedSidebarScroll = undefined;

    // Scroll content to hash target after frame load
    if (this._clickedHash) {
      const target = document.getElementById(this._clickedHash.slice(1));
      if (target) {
        target.scrollIntoView({ block: "start", behavior: "instant" });
      }
      this._clickedHash = null;
    }
    this._clickedHref = null;
  };

  restoreScrollPosition = () => {
    const sidebar = this.element.querySelector("docs-sidebar");
    if (sidebar && this._savedSidebarScroll !== undefined) {
      sidebar.scrollTop = this._savedSidebarScroll;
    }
    const main = this.element.querySelector(".docs-main");
    if (main) {
      main.scrollTop = 0;
    }
  };

  scrollToCurrent = () => {
    const currentLink = this.navTarget.querySelector("a.current");
    if (!currentLink) return;

    // Open the parent <details> if it's closed
    const details = currentLink.closest("details");
    if (details && !details.open) {
      details.open = true;
    }

    // Scroll the link into view within the sidebar
    const sidebar = this.element.querySelector("docs-sidebar");
    if (sidebar) {
      currentLink.scrollIntoView({ block: "center", behavior: "instant" });
    }
  };

  updateCurrent = () => {
    // Use the frame's current src as the path, falling back to clicked href
    const frame = this.hasFrameTarget ? this.frameTarget : null;
    const frameSrc = frame?.src;
    const currentUrl = frameSrc
      ? new URL(frameSrc, window.location.origin)
      : null;
    const currentPath =
      currentUrl?.pathname || this._clickedHref || window.location.pathname;
    const currentHash =
      this._clickedHash || currentUrl?.hash || window.location.hash;

    const links = this.navTarget.querySelectorAll("a");

    links.forEach((link) => {
      const url = new URL(link.href, window.location.origin);

      if (url.hash) {
        // Endpoint sub-items: match pathname + hash
        const matches =
          url.pathname === currentPath && url.hash === currentHash;
        link.classList.toggle("current", matches);
      } else if (
        link.hasAttribute("data-turbo-frame") ||
        link.closest(".docs-nav-section > ul")
      ) {
        // Tag-level and section links: match pathname only
        link.classList.toggle("current", url.pathname === currentPath);
      } else {
        link.classList.remove("current");
      }
    });
  };
}
